// Order Management Page
// Displays order list with filtering, detail panel, and state transitions
// Includes LLM detection effectiveness analytics dashboard

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDateTime } from '../utils/dateFormatters';
import { formatMoney } from '../utils/formatting';
import { orderService } from '../services/orders';
import type {
  OrderRecord,
  OrderDetailResponse,
  OrderStatus,
  OrderSourceType,
  SageTrackingResponse,
} from '../types/orders';
import {
  ORDER_STATUS_LABELS,
  ORDER_STATUS_COLORS,
  ORDER_ACTION_LABELS,
  STATUS_ACTIONS,
} from '../types/orders';
import OrderDetectionDashboard from '../components/orders/OrderDetectionDashboard';
import ConfidenceBadge from '../components/portal/ConfidenceBadge';
import styles from './OrdersPage.module.css';

type ViewMode = 'orders' | 'analytics';

type StatusFilter = OrderStatus | 'all' | 'active';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted_to_sage', label: 'Submitted to Sage' },
  { value: 'confirmed_in_sage', label: 'Confirmed in Sage' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'order_completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

type SortField = 'created_at' | 'updated_at' | 'customer_name' | 'status' | 'subtotal';

const SOURCE_TYPE_OPTIONS: { value: '' | OrderSourceType; label: string }[] = [
  { value: '', label: 'All Sources' },
  { value: 'email_llm', label: 'Email/LLM' },
  { value: 'manual', label: 'Manual' },
  { value: 'quote_conversion', label: 'Quote Conversion' },
];

function StatusBadge({ status }: { status: OrderStatus }) {
  const color = ORDER_STATUS_COLORS[status];
  return (
    <span
      className={styles.statusBadge}
      style={{ background: `${color}15`, color }}
    >
      <span className={styles.statusDot} style={{ background: color }} />
      {ORDER_STATUS_LABELS[status]}
    </span>
  );
}

function SourceBadge({ source }: { source: string }) {
  const labels: Record<string, string> = {
    email_llm: 'Email/LLM',
    manual: 'Manual',
    quote_conversion: 'Quote',
  };
  return <span className={styles.sourceBadge}>{labels[source] || source}</span>;
}

// NOTE: a ReasonDialog component used to live here. Its only caller was the
// "Cancel Order" action, which has been removed (see STATUS_ACTIONS in
// types/orders.ts) because it cancelled our record only and left the Sage
// sales order live. If a real Sage-side cancel lands (kanban e2ed881e), it
// needs a confirmation that states plainly what it deletes in Sage, not a
// free-text reason box.

// ============================================================================
// Sage Order Number Dialog Component
// ============================================================================

function SageOrderDialog({
  onSubmit,
  onCancel,
  loading,
}: {
  onSubmit: (sageOrderNo: string) => void;
  onCancel: () => void;
  loading: boolean;
}) {
  const [sageOrderNo, setSageOrderNo] = useState('');

  return (
    <div className={styles.dialogOverlay} onClick={onCancel}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.dialogTitle}>Confirm Sage Order</h3>
        <input
          className={styles.dialogInput}
          type="text"
          placeholder="Enter Sage Order Number..."
          value={sageOrderNo}
          onChange={(e) => setSageOrderNo(e.target.value)}
          autoFocus
        />
        <div className={styles.dialogActions}>
          <button
            className={styles.actionBtn}
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className={`${styles.actionBtn} ${styles.primary}`}
            onClick={() => onSubmit(sageOrderNo)}
            disabled={!sageOrderNo.trim() || loading}
          >
            {loading ? 'Processing...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Order Detail Panel
// ============================================================================

function SageTrackingSection({ orderId }: { orderId: string }) {
  const [tracking, setTracking] = useState<SageTrackingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchTracking = useCallback(async () => {
    setLoading(true);
    try {
      const data = await orderService.getSageTracking(orderId);
      setTracking(data);
    } catch (err) {
      console.error('Failed to fetch Sage tracking:', err);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  // Auto-fetch when the section is first shown
  useEffect(() => {
    fetchTracking();
  }, [fetchTracking]);

  if (loading && !tracking) {
    return (
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Sage Tracking</h3>
        <p style={{ color: '#6b7280', fontSize: '0.8125rem' }}>Loading Sage data...</p>
      </div>
    );
  }

  if (!tracking) return null;

  const { submission, validation } = tracking;

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>
        Sage Tracking
        <button
          className={styles.sageRefreshBtn}
          onClick={fetchTracking}
          disabled={loading}
          title="Refresh Sage data"
        >
          {loading ? '...' : 'Refresh'}
        </button>
      </h3>

      {/* Submission Info */}
      {submission ? (
        <div className={styles.sageBlock}>
          <div className={styles.sageBlockHeader}>
            <span className={styles.sageBlockLabel}>API Submission</span>
            {submission.latency_ms != null && (
              <span className={styles.sageLatency}>{submission.latency_ms}ms</span>
            )}
          </div>
          <div className={styles.infoGrid}>
            {submission.sage_order_no && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Sage Order #</span>
                <span className={styles.infoValue}>{submission.sage_order_no}</span>
              </div>
            )}
            {submission.submitted_at && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Submitted At</span>
                <span className={styles.infoValue}>{formatDateTime(submission.submitted_at)}</span>
              </div>
            )}
            {submission.transition_notes && (
              <div className={`${styles.infoItem} ${styles.sageFullWidth}`}>
                <span className={styles.infoLabel}>Notes</span>
                <span className={styles.infoValue}>{submission.transition_notes}</span>
              </div>
            )}
          </div>

          {/* Sage API Response - collapsible */}
          {submission.sage_response && (
            <div className={styles.sageResponseWrapper}>
              <button
                className={styles.sageToggleBtn}
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? 'Hide' : 'Show'} API Response
              </button>
              {expanded && (
                <pre className={styles.sageResponsePre}>
                  {JSON.stringify(submission.sage_response, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      ) : (
        <p style={{ color: '#6b7280', fontSize: '0.8125rem', margin: '0 0 0.75rem' }}>
          No submission data recorded yet.
        </p>
      )}

      {/* Validation Info */}
      {validation && (
        <div className={styles.sageBlock}>
          <div className={styles.sageBlockHeader}>
            <span className={styles.sageBlockLabel}>Sage Table Verification</span>
            <span
              className={`${styles.sageVerifyBadge} ${
                validation.found ? styles.sageVerifyFound : styles.sageVerifyNotFound
              }`}
            >
              {validation.found ? 'Found in Sage' : 'Not Found'}
            </span>
          </div>
          {validation.found && validation.table_name && (
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
              Table: <code style={{ background: '#f3f4f6', padding: '0.125rem 0.375rem', borderRadius: '3px' }}>{validation.table_name}</code>
            </div>
          )}
          {validation.error && !validation.found && (
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>
              {validation.error}
            </div>
          )}
          {validation.found && Array.isArray(validation.order_data?.records) && (
            <div className={styles.sageRecords}>
              <div style={{ fontSize: '0.6875rem', color: '#6b7280', marginBottom: '0.375rem' }}>
                {(validation.order_data.records as unknown[]).length} record(s) found
              </div>
              <pre className={styles.sageResponsePre} style={{ maxHeight: '200px' }}>
                {JSON.stringify(validation.order_data.records, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function OrderDetailPanel({
  detail,
  onClose,
  onTransition,
  actionLoading,
}: {
  detail: OrderDetailResponse;
  onClose: () => void;
  onTransition: (action: string, extra?: { reason?: string; sage_order_no?: string; notes?: string }) => void;
  actionLoading: boolean;
}) {
  const { order, line_items, history } = detail;
  const [showSageDialog, setShowSageDialog] = useState(false);

  const handleAction = (action: string) => {
    if (action === 'confirm_sage') {
      setShowSageDialog(true);
    } else {
      onTransition(action);
    }
  };

  const actions = STATUS_ACTIONS[order.status] || [];

  return (
    <div className={styles.detailOverlay} onClick={onClose}>
      <div className={styles.detailPanel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.detailHeader}>
          <div>
            <h2 className={styles.detailTitle}>{order.customer_name}</h2>
            <div className={styles.detailMeta}>
              <StatusBadge status={order.status} />
              {' '}
              <SourceBadge source={order.source_type} />
              {' '}
              Created {formatDate(order.created_at)}
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>
            &times;
          </button>
        </div>

        <div className={styles.detailBody}>
          {/* Order Info */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Order Information</h3>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Customer No</span>
                <span className={styles.infoValue}>{order.customer_no}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Salesperson</span>
                <span className={styles.infoValue}>{order.salesperson_name}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Payment Terms</span>
                <span className={styles.infoValue}>
                  {order.payment_terms || '-'}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Sage Order No</span>
                <span className={styles.infoValue}>
                  {order.sage_order_no || '-'}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Subtotal</span>
                <span className={`${styles.infoValue} ${styles.amount}`}>
                  {formatMoney(order.subtotal, '-')}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Freight</span>
                <span className={`${styles.infoValue} ${styles.amount}`}>
                  {formatMoney(order.freight_cost, '-')}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Total</span>
                <span className={`${styles.infoValue} ${styles.amount}`}>
                  {formatMoney(order.total, '-')}
                </span>
              </div>
              {order.confidence_bucket && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>AI Confidence</span>
                  <span className={styles.infoValue}>
                    <ConfidenceBadge bucket={order.confidence_bucket} />
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Line Items */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              Line Items ({line_items.length})
            </h3>
            {line_items.length > 0 ? (
              <table className={styles.lineItemsTable}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Item</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {line_items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.position}</td>
                      <td>
                        <strong>{item.item_code}</strong>
                        <br />
                        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {item.item_description}
                        </span>
                        {item.packaging && (
                          <span
                            style={{
                              fontSize: '0.6875rem',
                              color: '#9ca3af',
                              display: 'block',
                            }}
                          >
                            {item.packaging}
                          </span>
                        )}
                      </td>
                      <td>{item.quantity}</td>
                      <td className={styles.amount}>
                        {formatMoney(item.unit_price, '-')}
                      </td>
                      <td className={styles.amount}>
                        {formatMoney(item.line_total, '-')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                No line items yet.
              </p>
            )}
          </div>

          {order.cancelled_at && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Cancellation</h3>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Cancelled At</span>
                  <span className={styles.infoValue}>
                    {formatDateTime(order.cancelled_at)}
                  </span>
                </div>
                {order.cancel_reason && (
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Reason</span>
                    <span className={styles.infoValue}>
                      {order.cancel_reason}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sage Tracking - shown for submitted/confirmed/accepted/completed orders */}
          {(order.status !== 'draft' && order.status !== 'cancelled' || order.sage_submitted_at) && (
            <SageTrackingSection orderId={order.id} />
          )}

          {/* History Timeline */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              History ({history.length})
            </h3>
            <div className={styles.timeline}>
              {history.map((t) => (
                <div key={t.id} className={styles.timelineItem}>
                  <span
                    className={styles.timelineDot}
                    style={{
                      background: ORDER_STATUS_COLORS[t.to_status],
                    }}
                  />
                  <div className={styles.timelineContent}>
                    <div className={styles.timelineTransition}>
                      {t.from_status === t.to_status ? (
                        // Audit-only annotation (status unchanged): e.g. the
                        // Sage importer verdict recorded after submission.
                        <>Audit note &middot; {ORDER_STATUS_LABELS[t.to_status]}</>
                      ) : (
                        <>
                          {ORDER_STATUS_LABELS[t.from_status]} &rarr;{' '}
                          {ORDER_STATUS_LABELS[t.to_status]}
                        </>
                      )}
                    </div>
                    {t.notes && (
                      <div className={styles.timelineNotes}>{t.notes}</div>
                    )}
                    <div className={styles.timelineTime}>
                      {formatDateTime(t.created_at)} &middot; {t.trigger_type}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        {actions.length > 0 && (
          <div className={styles.actions}>
            {actions.map((action) => {
              const isPrimary =
                action === 'submit' ||
                action === 'confirm';
              return (
                <button
                  key={action}
                  className={`${styles.actionBtn} ${isPrimary ? styles.primary : ''}`}
                  onClick={() => handleAction(action)}
                  disabled={actionLoading}
                >
                  {ORDER_ACTION_LABELS[action] || action}
                </button>
              );
            })}
          </div>
        )}

        {/* Sage Order Number Dialog */}
        {showSageDialog && (
          <SageOrderDialog
            onSubmit={(sageOrderNo) => {
              onTransition('confirm_sage', { sage_order_no: sageOrderNo });
              setShowSageDialog(false);
            }}
            onCancel={() => setShowSageDialog(false)}
            loading={actionLoading}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main OrdersPage Component
// ============================================================================

export function OrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // The Detection Analytics tab is ADMIN-ONLY, matching its backing
  // /api/stats/orders/* routes (require_admin): this is the admin portal, so
  // only admins see anything here (owner decision, review d296ea47). A user
  // either sees the tab and can load it, or sees neither — never a tab that
  // 403s (kanban 03ce63fe). Role, not scope: analytics:view gates the PORTAL
  // Inbox Analytics tab, a different surface.
  const { user } = useAuth();
  const canViewAnalytics = user?.role === 'ADMIN';
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    searchParams.get('view') === 'analytics' && canViewAnalytics ? 'analytics' : 'orders',
  );
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedDetail, setSelectedDetail] =
    useState<OrderDetailResponse | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 50;

  // Toast state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Filter state
  const [sourceTypeFilter, setSourceTypeFilter] = useState<'' | OrderSourceType>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Sort state
  const [sortBy, setSortBy] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const statusFilter: StatusFilter =
    (searchParams.get('status') as StatusFilter) || 'active';

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        limit: pageSize,
        offset: page * pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      if (statusFilter !== 'all' && statusFilter !== 'active') {
        params.status = statusFilter;
      }
      if (statusFilter === 'active') {
        params.exclude_terminal = true;
      }
      if (sourceTypeFilter) {
        params.source_type = sourceTypeFilter;
      }
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      if (dateFrom) {
        params.date_from = new Date(dateFrom).toISOString();
      }
      if (dateTo) {
        // Set to end of day
        const d = new Date(dateTo);
        d.setHours(23, 59, 59, 999);
        params.date_to = d.toISOString();
      }

      const response = await orderService.listOrders(params as any);

      setOrders(response.orders);
      setTotal(response.total);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, sourceTypeFilter, searchQuery, dateFrom, dateTo, sortBy, sortOrder]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleFilterChange = (filter: StatusFilter) => {
    setSearchParams({ status: filter });
    setPage(0);
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(0);
  };

  const resetFilters = () => {
    setSourceTypeFilter('');
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
    setSortBy('created_at');
    setSortOrder('desc');
    setPage(0);
  };

  const totalPages = Math.ceil(total / pageSize);
  const hasActiveFilters = sourceTypeFilter !== '' || searchQuery !== '' || dateFrom !== '' || dateTo !== '';

  const handleOrderClick = async (orderId: string) => {
    try {
      const detail = await orderService.getOrder(orderId);
      setSelectedDetail(detail);
    } catch (error) {
      console.error('Failed to fetch order detail:', error);
    }
  };

  const handleTransition = async (
    action: string,
    extra?: { reason?: string; sage_order_no?: string; notes?: string },
  ) => {
    if (!selectedDetail) return;

    setActionLoading(true);
    try {
      const result = await orderService.transitionOrder(
        selectedDetail.order.id,
        {
          action,
          ...extra,
        },
      );
      setSelectedDetail(result);
      fetchOrders(); // Refresh list
    } catch (error: any) {
      const msg =
        error?.response?.data?.detail || 'Transition failed';
      setToast({ message: msg, type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewChange = (mode: ViewMode) => {
    setViewMode(mode);
    const newParams = new URLSearchParams(searchParams);
    if (mode === 'analytics') {
      newParams.set('view', 'analytics');
    } else {
      newParams.delete('view');
    }
    setSearchParams(newParams);
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Orders</h1>
          <div className={styles.counts}>
            <span className={styles.countBadge}>{total} total</span>
          </div>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.viewToggle}>
            <button
              className={`${styles.viewToggleBtn} ${viewMode === 'orders' ? styles.viewToggleActive : ''}`}
              onClick={() => handleViewChange('orders')}
            >
              Order List
            </button>
            {canViewAnalytics && (
              <button
                className={`${styles.viewToggleBtn} ${viewMode === 'analytics' ? styles.viewToggleActive : ''}`}
                onClick={() => handleViewChange('analytics')}
              >
                Detection Analytics
              </button>
            )}
            {canViewAnalytics && (
              // The general admin Readout (owner ask 2026-10-01, kanban 1140692e):
              // the Order Pipeline tab reads out this pipeline across sources,
              // Sage outcomes, open drafts, red flags, AI spend and benefits.
              <Link
                to="/readout?system=order_pipeline"
                className={styles.viewToggleBtn}
                data-testid="orders-readout-link"
              >
                Readout
              </Link>
            )}
          </div>
          {viewMode === 'orders' && (
            <button className={styles.refreshBtn} onClick={fetchOrders}>
              Refresh
            </button>
          )}
        </div>
      </div>

      {/* Analytics View */}
      {viewMode === 'analytics' && canViewAnalytics && <OrderDetectionDashboard />}

      {/* Order List View */}
      {viewMode === 'orders' && (
        <>
          {/* Status Filters */}
          <div className={styles.statusTabs}>
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                className={`${styles.statusTab} ${
                  statusFilter === f.value ? styles.active : ''
                }`}
                onClick={() => handleFilterChange(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Filter Controls */}
          <div className={styles.filtersCard}>
            <div className={styles.filtersHeader}>
              <span>Filters</span>
              {hasActiveFilters && (
                <button className={styles.resetBtn} onClick={resetFilters}>
                  Reset
                </button>
              )}
            </div>
            <div className={styles.filtersGrid}>
              <div className={styles.filterGroup}>
                <label>Search</label>
                <input
                  type="text"
                  placeholder="Customer, Sage #..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                />
              </div>
              <div className={styles.filterGroup}>
                <label>Source Type</label>
                <select
                  value={sourceTypeFilter}
                  onChange={(e) => { setSourceTypeFilter(e.target.value as '' | OrderSourceType); setPage(0); }}
                >
                  {SOURCE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className={styles.filterGroup}>
                <label>From Date</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                />
              </div>
              <div className={styles.filterGroup}>
                <label>To Date</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className={styles.tableWrapper}>
            {loading ? (
              <div className={styles.loading}>Loading orders...</div>
            ) : orders.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>📦</div>
                <h3 className={styles.emptyTitle}>No orders found</h3>
                <p className={styles.emptyText}>
                  {hasActiveFilters
                    ? 'No orders match the current filters.'
                    : statusFilter === 'all'
                      ? 'No orders have been created yet.'
                      : `No orders with status "${statusFilter}".`}
                </p>
              </div>
            ) : (
              <>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th
                        className={styles.sortable}
                        onClick={() => handleSort('status')}
                      >
                        Status
                        {sortBy === 'status' && (
                          <span className={styles.sortIndicator}>
                            {sortOrder === 'desc' ? ' ↓' : ' ↑'}
                          </span>
                        )}
                      </th>
                      <th
                        className={styles.sortable}
                        onClick={() => handleSort('customer_name')}
                      >
                        Customer
                        {sortBy === 'customer_name' && (
                          <span className={styles.sortIndicator}>
                            {sortOrder === 'desc' ? ' ↓' : ' ↑'}
                          </span>
                        )}
                      </th>
                      <th>Salesperson</th>
                      <th>Source</th>
                      <th
                        className={styles.sortable}
                        onClick={() => handleSort('subtotal')}
                      >
                        Total
                        {sortBy === 'subtotal' && (
                          <span className={styles.sortIndicator}>
                            {sortOrder === 'desc' ? ' ↓' : ' ↑'}
                          </span>
                        )}
                      </th>
                      <th>Sage #</th>
                      <th
                        className={styles.sortable}
                        onClick={() => handleSort('created_at')}
                      >
                        Created
                        {sortBy === 'created_at' && (
                          <span className={styles.sortIndicator}>
                            {sortOrder === 'desc' ? ' ↓' : ' ↑'}
                          </span>
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr
                        key={order.id}
                        onClick={() => handleOrderClick(order.id)}
                      >
                        <td>
                          <StatusBadge status={order.status} />
                        </td>
                        <td>
                          <strong>{order.customer_name}</strong>
                          <br />
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            {order.customer_no}
                          </span>
                        </td>
                        <td>{order.salesperson_name}</td>
                        <td>
                          <SourceBadge source={order.source_type} />
                        </td>
                        <td className={styles.amount}>
                          {formatMoney(order.total || order.subtotal, '-')}
                        </td>
                        <td>
                          {order.sage_order_no || (
                            <span style={{ color: '#9ca3af' }}>-</span>
                          )}
                        </td>
                        <td>{formatDate(order.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className={styles.pagination}>
                  <span>
                    Page {page + 1} of {totalPages} ({total} total)
                  </span>
                  <div className={styles.paginationButtons}>
                    <button
                      className={styles.paginationBtn}
                      disabled={page === 0}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      Previous
                    </button>
                    <button
                      className={styles.paginationBtn}
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Detail Panel */}
          {selectedDetail && (
            <OrderDetailPanel
              detail={selectedDetail}
              onClose={() => setSelectedDetail(null)}
              onTransition={handleTransition}
              actionLoading={actionLoading}
            />
          )}
        </>
      )}

      {/* Toast Notification */}
      {toast && (
        <div
          className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : styles.toastSuccess}`}
          onClick={() => setToast(null)}
          onAnimationEnd={() => {
            // Auto-dismiss after animation if desired
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            {toast.type === 'error' ? (
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            ) : (
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            )}
          </svg>
          {toast.message}
        </div>
      )}
    </div>
  );
}
